<script setup>
import { $api } from '@/services';
import { ref, defineEmits, watch, defineProps } from 'vue';
import { useStore } from 'vuex';

const store = useStore();

const emit = defineEmits(['success', 'close']);
const props = defineProps(['content', 'src']);

const note = ref(props.content);
const src = ref(props.src);

watch(
	() => props.content,
	(value) => {
		note.value = value;
	}
);

watch(
	() => props.src,
	(value) => {
		src.value = value;
	}
);

const image = ref({});
const key = ref('');

function removeSrc() {
	src.value = '';
}
function openModelUpload() {
	const target = document.getElementById('upload-images-notification');
	target ? target.click() : '';
}
function closeUpload() {
	const target = document.getElementById('upload-images-notification');
	target ? (target.value = null) : '';
}

function uploadChange(event) {
	image.value = event.target.files[0];
	src.value = URL.createObjectURL(event.target.files[0]);
	closeUpload();
}
async function uploadToS3(url, payload) {
	try {
		const { data } = await $api.file.uploadToS3(url, payload);
		const keyData = data.toString().match('<Key>(.*)</Key>');
		key.value = keyData && keyData.length ? keyData[1] : '';
	} catch (e) {
		store.dispatch('setAlertMessage', {
			message:
				e?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
}
async function getSignatures() {
	const {
		data: { data },
	} = await $api.file.getSignatures({
		mime_types: [image.value.type],
		prefix: 'notice_attachment',
	});
	if (data && data.length) {
		for (let i = 0; i < data.length; i++) {
			let formData = new FormData();
			formData.append('AWSAccessKeyId', data[0]['AWSAccessKeyId'] || '');
			formData.append('key', data[i].key || '');
			formData.append('acl', data[i].acl || '');
			formData.append('Content-Type', data[i]['Content-Type'] || '');
			formData.append('x-amz-signature', data[i].signature || '');
			formData.append('Signature', data[i].signature || '');
			formData.append('Policy', data[i].policy || '');
			formData.append(
				'success_action_status',
				data[i].success_action_status || ''
			);
			formData.append('file', image.value);
			await uploadToS3(data[i].action, formData);
		}
	}
}

async function submit() {
	try {
		if (image.value.name) {
			await getSignatures();
		}
		const payload = {
			content: note.value,
			asset_key: key.value,
		};
		await $api.org.submitTuitionNote(payload);
		emit('success');
	} catch (e) {
		store.dispatch('setAlertMessage', {
			message:
				e?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
}
</script>
<template>
	<input
		type="file"
		hidden
		id="upload-images-notification"
		@change="uploadChange"
		accept="image/png, image/jpeg, image/jpg"
	/>
	<div class="fixed inset-y-0 right-0 flex side-over z-50 w-screen md:w-2/5">
		<div
			class="
				h-full
				w-full
				flex flex-col
				bg-white
				shadow-xl
				overflow-y-auto
			"
		>
			<div class="px-6 py-8 bg-blue-50">
				<div class="flex items-start justify-between">
					<div
						class="flex items-center gap-2"
						@click="$emit('close')"
					>
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								fill-rule="evenodd"
								clip-rule="evenodd"
								d="M13.6308 5.68726L15.8838 3.43422C16.7496 2.56844 17.1825 2.13555 17.6895 1.99477C18.0396 1.89758 18.4095 1.89758 18.7596 1.99477C19.2667 2.13555 19.6996 2.56844 20.5653 3.43423C21.4311 4.30001 21.864 4.7329 22.0048 5.23996C22.102 5.59003 22.102 5.95997 22.0048 6.31005C21.864 6.81711 21.4311 7.25 20.5653 8.11578L18.3384 10.3427C16.3859 9.22291 14.7651 7.6144 13.6308 5.68726ZM12.1764 7.14163L3.8659 15.4521C3.44084 15.8772 3.22831 16.0897 3.08857 16.3508C2.94884 16.6119 2.88989 16.9066 2.772 17.4961L1.98622 21.425C1.91969 21.7576 1.88643 21.9239 1.98104 22.0185C2.07564 22.1131 2.24194 22.0799 2.57452 22.0134L2.57455 22.0134L2.57456 22.0134L6.50349 21.2276L6.50351 21.2276C7.09295 21.1097 7.38767 21.0507 7.64876 20.911C7.90986 20.7713 8.12239 20.5587 8.54743 20.1337L8.54745 20.1337L16.8803 11.8008C14.9786 10.6167 13.3722 9.02142 12.1764 7.14163Z"
								fill="#94A3B8"
							/>
						</svg>
						<div class="text-2xl font-bold text-neutral-700">
							<div>Chỉnh sửa cài đặt</div>
						</div>
					</div>
					<div class="ml-3 h-7 flex items-center">
						<button
							class="
								rounded-md
								text-neutral-400
								hover:text-neutral-700
							"
							type="button"
							@click="$emit('close')"
						>
							<span class="sr-only">Close panel</span>
							<svg
								aria-hidden="true"
								class="h-6 w-6"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M6 18L18 6M6 6l12 12"
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>
			<div
				class="sideover-content mt-6 relative flex-1 px-4 sm:px-6 pb-20"
			>
				<div>
					<div class="input-group">
						<div class="text-neutral-600 font-semibold mb-2">
							Mã QR
						</div>
						<div class="text-sm font-normal text-neutral-500 mb-2">
							Trung tâm cung cấp mã QR để phụ huynh có thể thanh
							toán học phí nhanh chóng hơn
						</div>
						<div v-if="src" class="flex items-center gap-4">
							<div class="rounded-lg relative">
								<div
									class="
										absolute
										top-2
										right-2
										cursor-pointer
									"
									@click="removeSrc"
								>
									<svg
										width="24"
										height="24"
										viewBox="0 0 12 12"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<g opacity="0.5">
											<rect
												width="12"
												height="12"
												rx="6"
												fill="black"
											/>
										</g>
										<path
											fill-rule="evenodd"
											clip-rule="evenodd"
											d="M3.73483 3.73483C3.88128 3.58839 4.11872 3.58839 4.26517 3.73483L6.25 5.71967L8.23484 3.73483C8.38128 3.58839 8.61872 3.58839 8.76516 3.73483C8.91161 3.88128 8.91161 4.11872 8.76516 4.26517L6.78033 6.25L8.76516 8.23484C8.91161 8.38128 8.91161 8.61872 8.76516 8.76516C8.61872 8.91161 8.38128 8.91161 8.23484 8.76516L6.25 6.78033L4.26517 8.76516C4.11872 8.91161 3.88128 8.91161 3.73483 8.76516C3.58839 8.61872 3.58839 8.38128 3.73483 8.23484L5.71967 6.25L3.73483 4.26517C3.58839 4.11872 3.58839 3.88128 3.73483 3.73483Z"
											fill="white"
										/>
									</svg>
								</div>
								<img
									style="width: 200px; height: 200px"
									:src="src"
									alt=""
								/>
							</div>

							<button
								@click="openModelUpload"
								type="button"
								class="
									button-p
									text-center
									bg-none
									shadow-none
									flex
									items-center
									justify-center
									btn--text
									w-fit
									gap-2
								"
							>
								<svg
									width="16"
									height="16"
									viewBox="0 0 16 16"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M8 0C6.57359 0 5.31176 0.560456 4.40576 1.34182C3.63985 2.00237 3.08479 2.8617 2.94223 3.725C1.26586 4.09512 0 5.55511 0 7.31818C0 9.36599 1.70754 11 3.78125 11H7.5V5.70711L5.35355 7.85355C5.15829 8.04882 4.84171 8.04882 4.64645 7.85355C4.45118 7.65829 4.45118 7.34171 4.64645 7.14645L7.64645 4.14645C7.84171 3.95118 8.15829 3.95118 8.35355 4.14645L11.3536 7.14645C11.5488 7.34171 11.5488 7.65829 11.3536 7.85355C11.1583 8.04882 10.8417 8.04882 10.6464 7.85355L8.5 5.70711V11H12.6875C14.5023 11 16 9.56949 16 7.77273C16 6.13656 14.7581 4.80411 13.1657 4.57898C12.9233 1.99944 10.6902 0 8 0Z"
										fill="currentColor"
									/>
									<path
										fill-rule="evenodd"
										clip-rule="evenodd"
										d="M7.5 14.5V11H8.5V14.5C8.5 14.7761 8.27614 15 8 15C7.72386 15 7.5 14.7761 7.5 14.5Z"
										fill="currentColor"
									/>
								</svg>
								Tải lên ảnh khác
							</button>
						</div>
						<div
							v-else
							class="
								w-full
								text-neutral-500
								flex flex-col
								items-center
								justify-senter
								text-center
								gap-2
								rounded-lg
								p-8
								cursor-pointer
								border-2 border-dashed
								hover:border-yellow-500
							"
							@click="openModelUpload"
						>
							<svg
								class="text-yellow-500"
								width="48"
								height="48"
								viewBox="0 0 48 48"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									fill-rule="evenodd"
									clip-rule="evenodd"
									d="M24 0C19.7208 0 15.9353 1.68137 13.2173 4.02545C10.9196 6.0071 9.25438 8.5851 8.82668 11.175C3.79759 12.2853 0 16.6653 0 21.9545C0 28.098 5.12261 33 11.3438 33H22.5V17.1213L16.0607 23.5607C15.4749 24.1464 14.5251 24.1464 13.9393 23.5607C13.3536 22.9749 13.3536 22.0251 13.9393 21.4393L22.9393 12.4393C23.5251 11.8536 24.4749 11.8536 25.0607 12.4393L34.0607 21.4393C34.6464 22.0251 34.6464 22.9749 34.0607 23.5607C33.4749 24.1464 32.5251 24.1464 31.9393 23.5607L25.5 17.1213V33H38.0625C43.507 33 48 28.7085 48 23.3182C48 18.4097 44.2743 14.4123 39.4971 13.7369C38.7699 5.99831 32.0707 0 24 0Z"
									fill="currentColor"
								/>
								<path
									fill-rule="evenodd"
									clip-rule="evenodd"
									d="M22.5 43.5V33H25.5V43.5C25.5 44.3284 24.8284 45 24 45C23.1716 45 22.5 44.3284 22.5 43.5Z"
									fill="currentColor"
								/>
							</svg>
							<div class="font-semibold">
								Tải lên Mã QR của Trung tâm
							</div>
							<div class="text-sm">PNG, JPG</div>
						</div>
					</div>
				</div>
				<div class="mt-8">
					<div class="input-group">
						<div class="text-neutral-600 font-semibold mb-2">
							Ghi chú
						</div>
						<div class="text-sm font-normal text-neutral-500 mb-2">
							Trung tâm có thể ghi chú cho phụ huynh những thông
							tin liên quan đến thông báo học phí, ghi chú này sẽ
							được gửi cho phụ huynh
						</div>
						<textarea
							rows="8"
							v-model="note"
							class="bg-white"
							placeholder="Ghi chú của trung tâm"
						/>
					</div>
				</div>
			</div>
			<div
				class="
					px-6
					py-4
					border-t border-yellow-50
					flex
					justify-end
					gap-6
				"
			>
				<div class="flex justify-end">
					<button
						class="button-p text-center w-32"
						type="button"
						@click="submit"
					>
						Xác nhận
					</button>
				</div>
			</div>
		</div>
	</div>
</template>
